import { useState } from 'preact/hooks'
import type { ContactActivityItemDto } from '@/api/contactDetail'
import { BRAND_CLASSES } from '@/constants/theme'
import { Icon } from './contactShared'
import { relativeTime, formatDuration, sessionDisplayTitle, ICONS } from './contactFormat'

// A Web session with a server-side LandingPageId is a Demand Webs landing page; everything else is
// a generic website visit (SessionKind may be absent on older data — treat that as Website).
function isLandingPageSession(item: ContactActivityItemDto): boolean {
  return item.SessionKind === 'Landing page'
}

// Customer-hosted landing pages carry a LandingPageId just like builder-hosted ones — that's what
// the external tracking snippet embeds (ORB-494) — so SessionKind can't separate them; only the
// server-resolved mode can. Gated on 'External' alone, so null/'Builder'/unknown all render nothing.
function isExternalLandingPage(item: ContactActivityItemDto): boolean {
  return isLandingPageSession(item) && item.LandingPageMode === 'External'
}

// Top-level "kind" badge labelling each row's category on mixed views (All, the overview rail).
// Suppressed for whichever kind the surrounding view is already scoped to, where it'd be redundant.
// A separate subtype chip sits alongside and always renders: Landing page/Website for sessions,
// the EmailType (Marketing/Transactional) for emails.
const KIND_BADGE: Partial<Record<ContactActivityItemDto['Kind'], { label: string; className: string }>> = {
  Web: { label: 'Session', className: BRAND_CLASSES.STATUS.DRAFT },
  Interaction: { label: 'Conversion', className: BRAND_CLASSES.STATUS.SUCCESS },
  Email: { label: 'Email', className: BRAND_CLASSES.STATUS.PENDING },
}

function KindBadge({ kind, suppress }: { kind: ContactActivityItemDto['Kind']; suppress?: ContactActivityItemDto['Kind'] }) {
  const meta = KIND_BADGE[kind]
  if (!meta || kind === suppress) return null
  return (
    <span class={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${meta.className}`}>
      {meta.label}
    </span>
  )
}

interface ActivityRailProps {
  items: ContactActivityItemDto[]
  showCampaignTag?: boolean
  // The kind whose top-level badge to suppress because the surrounding view is already scoped to it
  // (e.g. the Sessions sub-filter passes 'Web'). Undefined on mixed views (All, the overview rail),
  // where every row's kind badge shows. Does not affect the web subtype badge (Landing page/Website).
  suppressKindBadge?: ContactActivityItemDto['Kind']
  // Show the web subtype badge (Landing page / Website). False when the view is already scoped to one
  // subtype (Sessions > Landing pages / Website), where it'd be redundant; true on mixed views.
  showSessionSubtype?: boolean
  onRemoveFromTimeline?: (item: ContactActivityItemDto) => void
  // Where a quiz entry links: the Quiz tab of the lead opened inside that web (ORB-915). Absent, or
  // undefined for an item, leaves the title plain.
  quizHref?: (item: ContactActivityItemDto) => string | undefined
}

// Presentational merged-activity rail: point events + ranged/nested web sessions on one timeline.
export function ActivityRail({ items, showCampaignTag, suppressKindBadge, showSessionSubtype = true, onRemoveFromTimeline, quizHref }: ActivityRailProps) {
  return (
    <ol class="space-y-0">
      {items.map((item, i) => (
        <ActivityRow
          key={`${item.Kind}-${item.OccurredAt}-${i}`}
          item={item}
          last={i === items.length - 1}
          showCampaignTag={showCampaignTag}
          suppressKindBadge={suppressKindBadge}
          showSessionSubtype={showSessionSubtype}
          onRemoveFromTimeline={onRemoveFromTimeline}
          quizHref={quizHref?.(item)}
        />
      ))}
    </ol>
  )
}

function ActivityRow({ item, last, showCampaignTag, suppressKindBadge, showSessionSubtype, onRemoveFromTimeline, quizHref }: {
  item: ContactActivityItemDto
  last: boolean
  showCampaignTag?: boolean
  suppressKindBadge?: ContactActivityItemDto['Kind']
  showSessionSubtype?: boolean
  onRemoveFromTimeline?: (item: ContactActivityItemDto) => void
  quizHref?: string
}) {
  const [open, setOpen] = useState(false)
  const isWeb = item.Kind === 'Web'
  const isDeleted = !!item.DeletedInSourceAt
  const dot = isDeleted
    ? 'bg-amber-500'
    : item.Kind === 'Email'
      ? 'bg-slate-300 dark:bg-slate-600'
      : item.Kind === 'Web'
        ? 'bg-slate-400 dark:bg-slate-500'
        : 'bg-primary-600'

  const duration = formatDuration(item.DurationSeconds)

  return (
    <li class="relative pl-6 pb-5 last:pb-0">
      {!last && <span class="absolute left-[3px] top-3 bottom-0 w-px bg-slate-200 dark:bg-slate-700" aria-hidden="true" />}
      <span class={`absolute left-0 top-1.5 h-2 w-2 rounded-full ring-4 ring-white dark:ring-slate-900 ${dot}`} aria-hidden="true" />

      {isWeb ? (
        <div>
          <button type="button" onClick={() => setOpen(o => !o)} class="flex w-full items-start justify-between gap-3 text-left" aria-expanded={open}>
            <div class="text-sm">
              <span class="inline-flex items-center gap-1.5">
                <Icon path={ICONS.chevron} size={13} strokeWidth={2.5} class={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                <span class="font-medium text-slate-900 dark:text-white">{sessionDisplayTitle(item)}</span>
              </span>
              <KindBadge kind={item.Kind} suppress={suppressKindBadge} />
              {showSessionSubtype && (
                <span class={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${isLandingPageSession(item) ? BRAND_CLASSES.STATUS.INFO : BRAND_CLASSES.STATUS.DEFAULT}`}>
                  {isLandingPageSession(item) ? 'Landing page' : 'Website'}
                </span>
              )}
              {/* Deliberately outside showSessionSubtype: the Sessions > Landing pages sub-filter
                  narrows the subtype dimension, not the hosting one, so this badge still carries
                  information there and must survive the subtype badge being suppressed. */}
              {isExternalLandingPage(item) && (
                <span class={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${BRAND_CLASSES.STATUS.WARNING}`}>
                  External
                </span>
              )}
              <span class="text-slate-500 dark:text-slate-400">
                {item.Source ? ` · ${item.Source}` : ''}{item.PageCount ? ` · ${item.PageCount} page${item.PageCount === 1 ? '' : 's'}` : ''}{duration ? ` · ${duration}` : ''}
              </span>
              {item.Converted && (
                <span class="ml-1 inline-flex items-center gap-1 rounded bg-primary-50 dark:bg-primary-950/40 px-1.5 py-0.5 text-[11px] font-medium text-primary-700 dark:text-primary-400">
                  <Icon path={ICONS.check} size={10} strokeWidth={3} />converted
                </span>
              )}
            </div>
            <time class="shrink-0 text-xs text-slate-400 dark:text-slate-500 tabular-nums">{relativeTime(item.OccurredAt)}</time>
          </button>
          {open && item.Pages.length > 0 && (
            <ul class="mt-2 space-y-1.5 border-l border-slate-200 dark:border-slate-700 pl-3 text-xs text-slate-500 dark:text-slate-400">
              {item.Pages.map((p, i) => (
                <li key={i} class="flex items-center justify-between gap-3">
                  <span class="truncate">{p.Path}{p.Type === 'conversion' ? ' (conversion)' : ''}</span>
                  <time class="tabular-nums shrink-0">{relativeTime(p.At)}</time>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div class="flex items-start justify-between gap-3">
          <div class="text-sm">
            {isDeleted ? (
              <span class="inline-flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                <Icon path={'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z|M12 9v4M12 17h.01'} size={13} class="text-amber-500" />
                <span class="line-through">{item.Title}{item.Subtitle ? ` — ${item.Subtitle}` : ''}</span>
              </span>
            ) : item.IsQuiz ? (
              <>
                {quizHref
                  ? <a href={quizHref} class="font-medium text-primary-700 dark:text-primary-400 hover:underline">{item.Title}</a>
                  : <span class="font-medium text-slate-900 dark:text-white">{item.Title}</span>}
                {item.Subtitle && <span class="text-slate-600 dark:text-slate-300"> · {item.Subtitle}</span>}
              </>
            ) : (
              <>
                <span class="font-medium text-slate-900 dark:text-white">{item.Title}</span>
                {item.Subtitle && <span class="text-slate-600 dark:text-slate-300"> {item.Subtitle}</span>}
              </>
            )}
            <KindBadge kind={item.Kind} suppress={suppressKindBadge} />
            {item.Kind === 'Email' && item.EmailType && (
              <span class="ml-1 inline-flex items-center rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">{item.EmailType}</span>
            )}
            {showCampaignTag && item.CampaignName && (
              <span class="ml-1 inline-flex items-center rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">{item.CampaignName}</span>
            )}
            {isDeleted && (
              <span class="mt-0.5 block text-xs text-amber-700 dark:text-amber-500">
                Removed in source
                {onRemoveFromTimeline && item.ConferenceId && item.RegistrationId && (
                  <> · <button type="button" onClick={() => onRemoveFromTimeline(item)} class="font-medium underline-offset-2 hover:underline">Remove from timeline</button></>
                )}
              </span>
            )}
          </div>
          <time class="shrink-0 text-xs text-slate-400 dark:text-slate-500 tabular-nums">{relativeTime(item.OccurredAt)}</time>
        </div>
      )}
    </li>
  )
}
